import React from 'react';

import {Bar, BarChart as RechartsBarChart, CartesianGrid, ReferenceLine, Tooltip, XAxis, YAxis} from 'recharts';
import {Moment} from 'moment';
import TooltipContent, {ChartType} from './TooltipContent';
import {BaseChartData, ChartProps, VisibleAttribute} from '../../types/Chart';
import _ from 'lodash';
import {observer} from 'mobx-react';
import {colorToString} from '../../utility/ColorUtils';
import {kpiPackagesMatch} from '../../stores/GeneralLineDiagramStore';
import {BlackColor, DarkGreyColor} from '../../styles/colors';

type RechartsBarChartProps = typeof RechartsBarChart['defaultProps'];

interface BarChartProps extends ChartProps {
    data: BarChartData[];
    vertical: boolean;
    onBarClick: (payload: BarChartData) => void;
    allowBarAnimation?: boolean;
    currencyFormatting: (value: number) => string;
}

export interface BarChartData extends BaseChartData {
    name: string;
    date?: Moment; // just to have it in payload later [click events on charts]
}

const roundLowNumbers = (resultNumber: number): number => {
    if (resultNumber < 1) {
        return Math.round(resultNumber * 1000) / 1000;
    }
    return resultNumber;
};

@observer
export default class BarChart extends React.Component<BarChartProps> {
    private renderBar = (barAttribute: VisibleAttribute) => {
        const {useAbsValue} = barAttribute;
        const {isCompareDiagram} = barAttribute.kpiRequestInfo;
        const color = colorToString(barAttribute.color);
        return (
            <Bar
                isAnimationActive={this.props.allowBarAnimation === true}
                maxBarSize={40}
                dataKey={(singleEntry: BarChartData) => {
                    const dataSet = singleEntry.data.find((data) => kpiPackagesMatch(data, barAttribute.kpiRequestInfo));
                    if (dataSet !== undefined) {
                        return roundLowNumbers(useAbsValue === false ? dataSet.relValue : dataSet.value);
                    }
                    return null;
                }}
                key={color + ':' + isCompareDiagram}
                fill={color}
                opacity={barAttribute.opacity === undefined ? 1 : barAttribute.opacity}
                shape={
                    isCompareDiagram ? <CompareBar opacity={barAttribute.opacity} fillColor={colorToString(barAttribute.color, true)} /> : undefined
                }
            />
        );
    };

    private onGraphBodyClick: RechartsBarChartProps['onClick'] = (e) => {
        if (!e) {
            return;
        }

        const activePayloads = e.activePayload;
        if (activePayloads) {
            const arrPayloads = activePayloads.map((activePayload: any) => activePayload.payload);
            if (arrPayloads.length > 0) {
                this.props.onBarClick(arrPayloads[0]);
            }
        }
    };

    public render(): React.ReactNode {
        let layout: RechartsBarChartProps['layout'];
        let xAxis: React.ReactNode;
        let yAxis: React.ReactNode;
        let marginLeft: number;

        const data = this.props.data;

        if (this.props.vertical) {
            layout = 'horizontal';
            marginLeft = 20;
            xAxis = <XAxis type="category" dataKey="name" />;
            yAxis = (
                <YAxis
                    type="number"
                    scale="linear"
                    tickFormatter={(a) => {
                        return String(_.round(a, 3));
                    }}
                />
            );
        } else {
            layout = 'vertical';
            marginLeft = 100;
            xAxis = <XAxis type="number" scale="linear" />;
            yAxis = <YAxis type="category" dataKey="name" interval={0} tick={<CustomizedAxisTick />} />;
        }
        return (
            <RechartsBarChart
                barGap={1}
                barCategoryGap={1}
                onClick={this.onGraphBodyClick}
                layout={layout}
                width={this.props.width}
                height={this.props.height}
                data={data}
                margin={{top: 0, right: 0, left: marginLeft, bottom: 0}}>
                <CartesianGrid vertical={this.props.vertical} horizontal={!this.props.vertical} />
                {xAxis}
                {yAxis}
                <Tooltip
                    content={
                        <TooltipContent
                            kpiPackageToString={this.props.kpiPackageToString}
                            visibleAttribute={this.props.visibleAttribute}
                            payload={undefined}
                            tooltipComparisonModeIfOneElementIsSelected={this.props.tooltipComparisonModeIfOneElementIsSelected}
                            chartType={ChartType.BAR_CHART}
                            currencyFormatting={this.props.currencyFormatting}
                        />
                    }
                />
                <ReferenceLine y={0} stroke={BlackColor} />
                {this.props.visibleAttribute.map(this.renderBar)}
            </RechartsBarChart>
        );
    }
}

interface CompareBarProps {
    opacity?: number;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    fill?: string;
    fillColor?: string;
}

interface CustomAxisTickProps {
    x?: number;
    y?: number;
    stroke?: string;
    payload?: any;
}

const CustomizedAxisTick: React.FunctionComponent<CustomAxisTickProps> = (props) => (
    <g transform={`translate(${props.x},${props.y})`}>
        <text style={{fontSize: 10}} x={0} y={0} dy={3} textAnchor="end" fill={DarkGreyColor}>
            {props.payload.value}
        </text>
    </g>
);

const CompareBar: React.FunctionComponent<CompareBarProps> = (props) => {
    const {x = 0, y = 0, width = 0, height = 0, fillColor, opacity} = props;

    if (!width || !height) {
        return null;
    }

    return <rect fill={fillColor} opacity={opacity === undefined ? 1 : opacity} x={x} y={y} width={width} height={Math.abs(height)} />;
};



// WEBPACK FOOTER //
// ./src/views/charts/BarChart.tsx